<template>
  <a-card :title="title" size="small" class="work-item-report-card">
    <a-spin :spinning="loading || regenerating">
      <div class="report-card-body">
        <p class="report-status-text">{{ statusText }}</p>
        <a-space>
          <a-button
            type="primary"
            :disabled="status !== 'ready'"
            :loading="downloading"
            @click="onDownload"
          >
            <template #icon><DownloadOutlined /></template>
            {{ downloadLabel }}
          </a-button>
          <a-button :loading="regenerating" @click="onRegenerate">
            <template #icon><ReloadOutlined /></template>
            {{ regenerateLabel }}
          </a-button>
        </a-space>
        <p v-if="error" class="report-error">{{ error }}</p>
      </div>
    </a-spin>
  </a-card>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'
import { message } from 'ant-design-vue'
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons-vue'
import {
  downloadWorkItemReport,
  getProjectReportsByProject,
  regenerateWorkItemReport,
  triggerBrowserDownload,
} from '@/modules/report/api/report'
import {
  useWorkItemReportCard,
  type WorkItemReportKind,
} from '@/modules/report/composables/useWorkItemReportCard'

const props = withDefaults(
  defineProps<{
    workItemId: number
    kind: WorkItemReportKind
    title?: string
    downloadLabel?: string
    regenerateLabel?: string
    fileNamePrefix?: string
  }>(),
  {
    title: '项目报告',
    downloadLabel: '下载报告',
    regenerateLabel: '重新生成',
    fileNamePrefix: 'report',
  },
)

const {
  status,
  loading,
  regenerating,
  downloading,
  error,
  regenerate,
  download,
} = useWorkItemReportCard({
  workItemId: toRef(props, 'workItemId'),
  listReports: (id) => getProjectReportsByProject(id, props.kind),
  regenerate: (id) => regenerateWorkItemReport(id, props.kind),
  download: async (id, reportId) => {
    const blob = await downloadWorkItemReport(id, props.kind, reportId)
    triggerBrowserDownload(blob, `${props.fileNamePrefix}_报告.pdf`)
  },
})

const statusText = computed(() => {
  switch (status.value) {
    case 'loading':
      return '正在加载报告状态…'
    case 'ready':
      return '报告已就绪，可以下载'
    case 'generating':
      return '报告生成中，请稍候…'
    case 'failed':
      return '报告生成失败，请重试'
    default:
      return '尚未生成报告，可点击重新生成'
  }
})

async function onDownload() {
  try {
    await download()
    message.success('报告下载已开始')
  } catch {
    message.error('报告下载失败，请稍后重试')
  }
}

async function onRegenerate() {
  await regenerate()
  if (!error.value) {
    message.success('已开始重新生成报告')
  } else {
    message.error('重新生成失败，请稍后重试')
  }
}
</script>

<style scoped>
.work-item-report-card {
  margin-top: 16px;
}
.report-card-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.report-status-text {
  margin: 0;
  color: rgba(0, 0, 0, 0.65);
}
.report-error {
  margin: 0;
  color: #cf1322;
}
</style>
